<script>
import OrgChartNode from './OrgChartNode.vue';
import SpellCheckedText from "@/components/SpellCheckedText.vue";

export default {
    name: "OrgChartTemp2",
    components: {
        OrgChartNode,
        SpellCheckedText
    },
    props: {
        orgChart: { type: Object, required: true },
        startPage: { type: Number, default: 1 },
    },
    data() {
        return {
            NODE_WIDTH: 120,
            NODE_HEIGHT: 165,
            LEVEL_GAP: 20,
            AVAILABLE_WIDTH: 737,
            AVAILABLE_HEIGHT: 942
        };
    },
    computed: {
        treeData() {
            return this.buildTree(this.orgChart.members);
        },
        treeScale() {
            const requiredWidth = this.getTreeWidth(this.treeData);
            const maxDepth = this.getMaxDepth(this.treeData);
            const requiredHeight = maxDepth * this.NODE_HEIGHT + (maxDepth - 1) * this.LEVEL_GAP;

            const widthScale = requiredWidth > this.AVAILABLE_WIDTH ? Math.max(0.3, this.AVAILABLE_WIDTH / requiredWidth) : 1;
            const heightScale = requiredHeight > this.AVAILABLE_HEIGHT ? Math.max(0.3, this.AVAILABLE_HEIGHT / requiredHeight) : 1;

            return Math.min(widthScale, heightScale);
        }
    },
    methods: {
        buildTree(members) {
            const map = {};
            const roots = [];

            if (!members || members.length === 0) return [];

            const membersCopy = members.map(member => ({
                ...member,
                sourceMember: member,
                children: []
            }));

            // Create a map for quick lookup
            membersCopy.forEach(member => {
                map[member.teamMemberId] = member;
            });

            // Attach children to parents
            membersCopy.forEach(member => {
                if (member.reportsToId && map[member.reportsToId]) {
                    map[member.reportsToId].children.push(member);
                } else {
                    roots.push(member);
                }
            });

            return roots;
        },
        getMaxDepth(nodes) {
            if (!nodes || nodes.length === 0) return 0;
            let maxDepth = 1;
            nodes.forEach(node => {
                if (node.children && node.children.length > 0) {
                    maxDepth = Math.max(maxDepth, 1 + this.getMaxDepth(node.children));
                }
            });
            return maxDepth;
        },
        getTreeWidth(nodes) {
            if (!nodes || nodes.length === 0) return 0;

            let totalWidth = 0;

            nodes.forEach(node => {
                if (!node.children || node.children.length === 0) {
                    totalWidth += this.NODE_WIDTH;
                } else {
                    // A parent's width is the max of its own width OR the sum of its children's widths
                    const childrenWidth = this.getTreeWidth(node.children);
                    totalWidth += Math.max(this.NODE_WIDTH, childrenWidth);
                }
            });

            return totalWidth;
        }
    }
};
</script>

<template>
    <div class="main-container page-unit" :style="{
        '--header-color': orgChart.style.colorScheme && orgChart.style.colorScheme.header,
        '--body-color': orgChart.style.colorScheme && orgChart.style.colorScheme.body,
        '--footer-color': orgChart.style.colorScheme && orgChart.style.colorScheme.footer,
        '--heading-color': orgChart.style.heading.color,
        '--heading-size': orgChart.style.heading.size + 'px',
        '--heading-weight': orgChart.style.heading.bold ? 'bold' : 'normal',
        '--heading-style': orgChart.style.heading.italic ? 'italic' : 'normal',
        '--heading-decoration': orgChart.style.heading.underline
            ? 'underline'
            : 'none',
        '--heading-font': orgChart.style.heading.font,
        '--label-color': orgChart.style.label.color,
        '--label-size': orgChart.style.label.size + 'px',
        '--label-weight': orgChart.style.label.bold ? 'bold' : 'normal',
        '--label-style': orgChart.style.label.italic ? 'italic' : 'normal',
        '--label-decoration': orgChart.style.label.underline
            ? 'underline'
            : 'none',
        '--label-font': orgChart.style.label.font,
        '--text-color': orgChart.style.text.color,
        '--text-size': orgChart.style.text.size + 'px',
        '--text-weight': orgChart.style.text.bold ? 'bold' : 'normal',
        '--text-style': orgChart.style.text.italic ? 'italic' : 'normal',
        '--text-decoration': orgChart.style.text.underline ? 'underline' : 'none',
        '--text-font': orgChart.style.text.font,
    }">
        <div class="banner top-banner">
            <div class="stripes-wrapper left">
                <div class="stripe-left" v-for="n in 8" :key="n"></div>
            </div>
            <h2 class="title">
                <SpellCheckedText :value="orgChart.title || ''" @input="orgChart.title = $event" />
            </h2>
        </div>

        <div class="content">
            <div class="tree-container">
                <ul class="tree" :style="{ transform: `scale(${treeScale})`, transformOrigin: 'top center' }">
                    <OrgChartNode v-for="node in treeData" :key="node.teamMemberId" :node="node" variant="circular" />
                </ul>
            </div>
        </div>

        <div class="banner bottom-banner">
            <div class="stripes-wrapper right">
                <div class="stripe-right" v-for="n in 8" :key="n"></div>
            </div>
            <span class="page-number">{{ String(startPage).padStart(2, '0') }}</span>
        </div>
    </div>
</template>

<style scoped lang="scss">
.main-container {
    width: 211mm;
    height: 296.83mm;
    background: white;
    display: flex;
    flex-direction: column;
    position: relative;
    box-sizing: border-box;
    overflow: hidden;

    &:hover {
        ::v-deep .node-card .photobox .photo-overlay {
            opacity: 1;
        }

        ::v-deep .node-card .photobox img {
            opacity: 0.9;
        }
    }
}

.banner {
    height: 60px;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    color: white;

    &.top-banner {
        background: var(--header-color);
    }

    &.bottom-banner {
        background: var(--footer-color);
    }

    .title {
        font-size: 18px;
        color: white;
        margin-bottom: 0px;
        font-weight: 700;
        font-family: Helvetica;
    }

    .page-number {
        position: absolute;
        right: 15px;
        bottom: 10px;
        font-size: 14px;
        font-weight: 700;
        color: white;
        font-family: Helvetica;
    }
}

.stripes-wrapper {
    position: absolute;
    display: flex;
    gap: 20px;
    top: 50%;
    transform: translateY(-50%);

    &.left {
        left: 50px;
    }

    &.right {
        right: 70px;
    }

    .stripe-right {
        width: 5px;
        height: 40px;
        background: #bec6d4;
        transform: rotate(45deg);
    }

    .stripe-left {
        width: 5px;
        height: 40px;
        background: #bec6d4;
        transform: rotate(-45deg);
    }
}

.content {
    flex: 1;
    padding: 30px 10px;
    display: flex;
    justify-content: center;
    overflow: hidden;
}

.tree-container {
    width: 100%;
    display: flex;
    justify-content: center;
}

.tree {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding-left: 0;
    margin: 0;

    ::v-deep ul {
        padding-top: 10px;
        position: relative;
        display: flex;
        justify-content: center;
        padding-left: 0;
        margin: 0;

        &::before {
            content: '';
            position: absolute;
            top: 0;
            left: 50%;
            border-left: 1px solid #777;
            width: 0;
            height: 10px;
        }
    }

    ::v-deep li {
        text-align: center;
        list-style-type: none;
        position: relative;
        padding: 10px 5px 0 5px;

        &::before,
        &::after {
            content: '';
            position: absolute;
            top: 0;
            right: 50%;
            border-top: 1px solid #777;
            width: 50%;
            height: 30px;
        }

        &::after {
            right: auto;
            left: 50%;
            border-left: 1px solid #777;
        }

        &:only-child::after,
        &:only-child::before {
            display: none;
        }

        &:only-child {
            padding-top: 0;
        }

        &:first-child::before,
        &:last-child::after {
            border: 0 none;
        }

        &:last-child::before {
            border-right: 1px solid #777;
            border-radius: 0 5px 0 0;
        }

        &:first-child::after {
            border-radius: 5px 0 0 0;
        }
    }
}
</style>
